import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { Icon } from "@iconify/react";
import getAccounts from "../../../controllers/user/getAccounts";
import CommonPaginationSection from "../../../components/common/CommonPaginationSection";
import { ListShimmer } from "../../../components/common/reportDashBShimmer/ListShimmer";
import AgentSupplierConfigModal from "./AgentSupplierConfigModal";

const DEFAULT_PER_PAGE = 10;
const SEARCH_BY =
  "firstName,lastName,email,phone,detail.agencyName,detail.companyName,detail.tradingName";

function agentDisplayName(agent) {
  const fullName = [agent?.firstName, agent?.lastName]
    .filter(Boolean)
    .join(" ");
  return (
    agent?.detail?.agencyName ||
    agent?.detail?.companyName ||
    agent?.detail?.tradingName ||
    fullName ||
    agent?.email ||
    "Agent"
  );
}

export default function AgentSupplierSettingsModal({ open, setOpen }) {
  const cancelTokenRef = useRef(null);
  const listScrollRef = useRef(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    total: 0,
  });

  const showList = open && !selectedAgent;
  const showConfig = open && Boolean(selectedAgent);

  const scrollListToTop = () => {
    if (listScrollRef.current) {
      listScrollRef.current.scrollTop = 0;
    }
  };

  const handleCloseAll = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    setPagination({ page: 1, perPage: DEFAULT_PER_PAGE, total: 0 });
    setAgents([]);
    setSelectedAgent(null);
    setOpen?.(false);
  };

  const handleBackToList = () => {
    setSelectedAgent(null);
  };

  const loadAgents = async ({
    keyword = debouncedSearch,
    page = pagination.page,
    perPage = pagination.perPage,
  } = {}) => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled due to new request.");
    }
    cancelTokenRef.current = axios.CancelToken.source();

    setLoading(true);
    const trimmed = keyword.trim();
    const params = {
      populate: "detail",
      limit: perPage,
      offset: (page - 1) * perPage,
      ...(trimmed ? { searchBy: SEARCH_BY, keyword: trimmed } : {}),
    };

    try {
      const res = await getAccounts(
        new URLSearchParams(params).toString(),
        cancelTokenRef.current.token,
      );
      if (res?.return) {
        const list = res?.data?.data || [];
        setAgents(list);
        setPagination((prev) => ({
          ...prev,
          page,
          perPage,
          total: res?.data?.meta?.total ?? list.length,
        }));
      } else {
        setAgents([]);
        setPagination((prev) => ({ ...prev, page, perPage, total: 0 }));
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        setAgents([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setDebouncedSearch("");
      setPagination({ page: 1, perPage: DEFAULT_PER_PAGE, total: 0 });
      setAgents([]);
      setSelectedAgent(null);
    }
  }, [open]);

  useEffect(() => {
    if (!showList) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showList]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!open) return;
    scrollListToTop();
    loadAgents({
      keyword: debouncedSearch,
      page: pagination.page,
      perPage: pagination.perPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedSearch, pagination.page, pagination.perPage]);

  const isEmpty = !loading && agents.length === 0;
  const hasSearch = Boolean(debouncedSearch);

  return (
    <>
      {showList
        ? ReactDOM.createPortal(
            <div className="fixed inset-0 z-[60] flex justify-end">
              <button
                type="button"
                aria-label="Close agent supplier settings"
                className="absolute inset-0 bg-black/40 border-0 cursor-pointer"
                onClick={handleCloseAll}
              />

              <aside
                className="relative z-[1] flex h-full w-full sm:w-[520px] md:w-[640px] max-w-full flex-col bg-white shadow-xl"
                role="dialog"
                aria-modal="true"
                aria-label="Agent supplier settings"
              >
                <div className="flex items-center justify-between gap-2 border-b border-[#E2E8F0] bg-[#0B2B4A] p-4 text-white shrink-0">
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <h2 className="text-lg sm:text-xl font-semibold text-white m-0">
                      Agent Supplier Settings
                    </h2>
                    <span className="text-xs sm:text-sm text-white/80">
                      {loading
                        ? "Loading agents..."
                        : `${pagination.total} agent${pagination.total === 1 ? "" : "s"}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleCloseAll}
                    className="shrink-0 rounded p-1 text-white hover:bg-white/10"
                    aria-label="Close"
                  >
                    <Icon icon="material-symbols:close" className="text-2xl" />
                  </button>
                </div>

                <div className="shrink-0 sticky top-0 z-10 bg-white border-b border-[#E2E8F0] p-4">
                  <div className="relative">
                    <Icon
                      icon="mdi:magnify"
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-[#6D7C94]"
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(event) => {
                        setSearchQuery(event.target.value);
                        setPagination((prev) => ({ ...prev, page: 1 }));
                      }}
                      placeholder="Search agents by name, email, or agency"
                      className="w-full h-12 pl-10 pr-4 border border-[#DBDBDB] rounded-lg placeholder:text-[#A4B1C1] focus:outline-none focus:border-primary1"
                    />
                  </div>
                </div>

                <div ref={listScrollRef} className="flex-1 overflow-y-auto p-4">
                  <div className="flex flex-col gap-3 min-h-full">
                    {loading ? (
                      <ListShimmer rows={6} />
                    ) : isEmpty ? (
                      <div className="flex flex-col items-center justify-center text-center gap-4 py-16 px-4">
                        <div className="w-14 h-14 rounded-full bg-[#F0F6FF] flex items-center justify-center">
                          <Icon
                            icon="mdi:account-search-outline"
                            className="text-3xl text-primary1"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <h3 className="text-base font-semibold text-[#1A1A1A] m-0">
                            {hasSearch ? "No agents found" : "No agents yet"}
                          </h3>
                          <p className="text-sm text-[#6D7C94] m-0 max-w-xs">
                            {hasSearch
                              ? "Try a different search term."
                              : "There are no agents available to configure."}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {agents.map((agent) => {
                          const name = agentDisplayName(agent);
                          const contactName = [
                            agent?.firstName,
                            agent?.lastName,
                          ]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <button
                              type="button"
                              key={agent?._id}
                              onClick={() => setSelectedAgent(agent)}
                              className="flex items-center justify-between gap-3 rounded-lg border border-[#E2E8F0] px-4 py-3 hover:border-primary1/40 transition-colors text-left bg-white"
                            >
                              <div className="flex flex-col min-w-0 gap-0.5">
                                <span className="text-sm font-semibold text-[#1A1A1A] truncate">
                                  {name}
                                </span>
                                {contactName && contactName !== name ? (
                                  <span className="text-xs text-[#6D7C94] truncate">
                                    {contactName}
                                  </span>
                                ) : null}
                                <span className="text-xs text-[#6D7C94] truncate">
                                  {agent?.email || "—"}
                                </span>
                              </div>
                              <Icon
                                icon="mdi:chevron-right"
                                className="text-xl text-[#A4B1C1] shrink-0"
                              />
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {!loading && pagination.total > 0 ? (
                      <CommonPaginationSection
                        perPage={pagination.perPage}
                        page={pagination.page}
                        total={pagination.total}
                        callback={(page, perPage) => {
                          scrollListToTop();
                          setPagination((prev) => ({
                            ...prev,
                            page,
                            perPage,
                          }));
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              </aside>
            </div>,
            document.body,
          )
        : null}

      <AgentSupplierConfigModal
        open={showConfig}
        agent={selectedAgent}
        onBack={handleBackToList}
        onClose={handleCloseAll}
      />
    </>
  );
}
